import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, Button, Table, Alert, Row, Col, Badge, InputGroup, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faSearch, 
  faEye,
  faBuilding,
  faLayerGroup,
  faSignOutAlt,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { propertyService } from '../../services/propertyService';

const PropertyGroups = () => {
  const navigate = useNavigate();
  const { userCompany, logout } = useAuth();
  
  const [propertyGroups, setPropertyGroups] = useState([]);
  const [propertyUnits, setPropertyUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filteredGroups, setFilteredGroups] = useState([]);
  
  useEffect(() => {
    fetchPropertyGroups();
  }, [userCompany]);

  useEffect(() => {
    let filtered = propertyGroups;
    
    // Filter by search term
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(group =>
        group.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(group => group.type === filterType);
    }
    
    setFilteredGroups(filtered);
  }, [searchTerm, filterType, propertyGroups]);

  const fetchPropertyGroups = async () => {
    try {
      setLoading(true);
      
      if (!userCompany) {
        setPropertyGroups([]);
        setPropertyUnits([]);
        return;
      }
      
      const [groupsData, unitsData] = await Promise.all([
        propertyService.getPropertyGroupsByCompany(userCompany),
        propertyService.getUnitsByCompany(userCompany)
      ]);
      
      setPropertyGroups(groupsData);
      setPropertyUnits(unitsData);
    } catch (error) {
      console.error('Error fetching property groups:', error);
      setError('Failed to fetch property groups');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (window.confirm('Are you sure you want to delete this property group? This will also delete all units in this group.')) {
      try {
        await propertyService.deletePropertyGroup(groupId);
        fetchPropertyGroups();
      } catch (error) {
        console.error('Error deleting property group:', error);
        setError('Failed to delete property group');
      }
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Property Groups</h2>
          <p className="text-muted mb-0">Manage property groups and units</p>
        </div>
        <div className="d-flex gap-2">
          <Button as={Link} to="/manager/property-groups/new" variant="primary">
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Property Group
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Search and Stats */}
      <Row className="mb-4">
        <Col lg={6}>
          <InputGroup>
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search property groups by name or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col lg={3}>
          <Form.Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="tower">Tower</option>
          </Form.Select>
        </Col>
        <Col lg={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h4 className="mb-1">{filteredGroups.length}</h4>
              <p className="text-muted mb-0">Total Groups</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Property Groups Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-transparent border-0">
          <h5 className="mb-0">Property Groups List</h5>
        </Card.Header>
        <Card.Body>
          {filteredGroups.length === 0 ? (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faBuilding} className="text-muted mb-3" size="3x" />
              <h5>No property groups found</h5>
              <p className="text-muted">
                {searchTerm || filterType !== 'all' ? 'No property groups match your search criteria.' : 'No property groups have been added yet.'}
              </p>
              {!searchTerm && filterType === 'all' && (
                <Button as={Link} to="/manager/property-groups/new" variant="primary">
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add First Property Group
                </Button>
              )}
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Group Name</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Units Count</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((group) => {
                  const unitsInGroup = propertyUnits.filter(unit => unit.groupId === group.id).length;
                  return (
                    <tr key={group.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faLayerGroup} className="text-primary me-2" />
                          <div>
                            <strong>{group.name}</strong>
                            {group.description && (
                              <div className="small text-muted">{group.description}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge bg="warning">
                          Tower
                        </Badge>
                      </td>
                      <td>
                        <div>
                          <div>{group.location}</div>
                          {group.address && (
                            <div className="small text-muted">{group.address}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge bg="secondary">{unitsInGroup}</Badge>
                      </td>
                      <td>
                        <Badge bg={group.status === 'active' ? 'success' : 'secondary'}>
                          {group.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button 
                            as={Link} 
                            to={`/manager/property-groups/${group.id}`} 
                            variant="outline-primary" 
                            size="sm"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                          <Button 
                            as={Link} 
                            to={`/manager/property-groups/${group.id}/edit`} 
                            variant="outline-warning" 
                            size="sm"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDeleteGroup(group.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PropertyGroups; 